import type { ErrorLike } from '@apollo/client';
import {
  faCalendarAlt,
  faClock,
  faCopy,
  faMapMarkerAlt,
  faUser,
  faVideo,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import copyToClipboard from 'copy-to-clipboard';
import React from 'react';
import styled from 'styled-components';
import {
  MboClassDetailsFragment,
  MboClassSubtype,
} from '../../../graphql/types';
import { useClassesQueryVariables } from '../../../hooks/useQueryParams';
import useTracking from '../../../hooks/useTracking';
import { BORDER_GRAY, ICON_SECONDARY, WHITE } from '../../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import LoadingPanel from '../../LoadingPanel';
import Button, { ButtonSize, ButtonVariant } from '../../reusable/Button';
import { LinkButton } from '../../reusable/Button/LinkButton';
import GenericError from '../../reusable/GenericError';
import LoadMore from '../../reusable/LoadMore';
import PaginationWaypoint from '../../reusable/PaginationWaypoint';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import Text, { TextColor, TextKind } from '../../reusable/Text';

const StyledNoClasses = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 128px;
`;

const StyledClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledClassCard = styled.div`
  background: ${WHITE};
  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;
  padding: 20px;
`;

const StyledClassHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const StyledClassMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledMetaIcon = styled.div`
  width: 16px;
  display: flex;
  justify-content: center;
`;

const StyledClassActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${BORDER_GRAY};
`;

const getClassTypeInfo = (subtype: MboClassSubtype) => {
  switch (subtype) {
    case MboClassSubtype.Encore:
      return { label: 'Encore', icon: faVideo };
    case MboClassSubtype.InPerson:
      return { label: 'In-Person', icon: faMapMarkerAlt };
    case MboClassSubtype.LiveVirtual:
      return { label: 'Virtual', icon: faVideo };
    default:
      return { label: 'In-Person', icon: faMapMarkerAlt };
  }
};

interface ClassListProps {
  classes?: MboClassDetailsFragment[];
  loading: boolean;
  isFetchingMore: boolean;
  hasNextPage: boolean | undefined;
  count?: number;
  error?: ErrorLike;
  loadMoreClasses: () => Promise<void>;
}

const ClassList: React.FC<ClassListProps> = ({
  classes,
  loading,
  isFetchingMore,
  hasNextPage,
  count,
  error,
  loadMoreClasses,
}) => {
  const [classesQueryVariables, setClassesQueryVariables] =
    useClassesQueryVariables();
  const { locationIds, studioIds } = classesQueryVariables;
  const { track } = useTracking();
  const { setSnackbar } = useSnackbarContext();

  const hasSidebarFilters =
    (locationIds && locationIds.length > 0) ||
    (studioIds && studioIds.length > 0);
  const filtersAreApplied = hasSidebarFilters;

  const resetFilters = () => {
    track(EventNames.ResetAll, {
      event_category: EventCategories.Classes,
      event_subcategory: EventSubCategories.Filters,
    });
    setClassesQueryVariables({
      ...classesQueryVariables,
      locationIds: [],
      studioIds: [],
    });
  };

  if (loading) {
    return <LoadingPanel noBorder testId="classList-loading" />;
  }

  if (error) {
    return <GenericError testId="classList-error" error={error} />;
  }
  if (count === 0) {
    return (
      <StyledNoClasses>
        <Text kind={TextKind.TitleXs}>No classes found.</Text>
        {filtersAreApplied && (
          <LinkButton onClick={resetFilters}>
            <Text kind={TextKind.BodyL}>Reset all filters</Text>
          </LinkButton>
        )}
      </StyledNoClasses>
    );
  }
  return (
    <StyledClassList data-testid="class-list">
      {classes?.map((node) => (
        <StyledClassCard key={`${node.id}-${node.startTime}-${node.name}`}>
          <StyledClassHeader>
            <Text kind={TextKind.TitleXs} color={TextColor.Primary}>
              {node.name}
            </Text>
          </StyledClassHeader>

          <StyledClassMeta>
            <StyledMetaItem>
              <StyledMetaIcon>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  color={ICON_SECONDARY}
                  fontSize={14}
                />
              </StyledMetaIcon>
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                {new Date(node.startTime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
            </StyledMetaItem>

            <StyledMetaItem>
              <StyledMetaIcon>
                <FontAwesomeIcon
                  icon={faClock}
                  color={ICON_SECONDARY}
                  fontSize={14}
                />
              </StyledMetaIcon>
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                {new Date(node.startTime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Text>
            </StyledMetaItem>

            <StyledMetaItem>
              <StyledMetaIcon>
                <FontAwesomeIcon
                  icon={faUser}
                  color={ICON_SECONDARY}
                  fontSize={14}
                />
              </StyledMetaIcon>
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                {node.staffFirstName} {node.staffLastName}
              </Text>
            </StyledMetaItem>

            <StyledMetaItem>
              <StyledMetaIcon>
                <FontAwesomeIcon
                  icon={getClassTypeInfo(node.subtype).icon}
                  color={ICON_SECONDARY}
                  fontSize={14}
                />
              </StyledMetaIcon>
              <Text kind={TextKind.BodyM} color={TextColor.Secondary}>
                {getClassTypeInfo(node.subtype).label}
              </Text>
            </StyledMetaItem>
          </StyledClassMeta>

          <StyledClassActions>
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Outline}
              onClick={() => {
                copyToClipboard(`https://fit.teamexos.com/class/${node.id}`);
                setSnackbar({
                  message: 'Link copied to clipboard',
                  icon: faCopy,
                  variant: SnackbarVariant.Success,
                });
              }}
            >
              <Text kind={TextKind.BodyM}>Copy Link</Text>
            </Button>
          </StyledClassActions>
        </StyledClassCard>
      ))}

      {!loading && hasNextPage && (
        <>
          <LoadMore
            error={error}
            isFetchingMore={isFetchingMore}
            loadMore={loadMoreClasses}
          />
          <PaginationWaypoint callback={loadMoreClasses} />
        </>
      )}
    </StyledClassList>
  );
};

export default ClassList;
